import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Plus, Pencil, Trash2, Boxes, Search } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  nome: "",
  categoria: "",
  descricao: "",
  valor_mensal_sugerido: 0,
  ativo: true,
  capacidade_carga_kg: "",
  elevacao_garfos_mm: "",
  largura_garfo_mm: "",
  comprimento_garfos_mm: "",
  tipo_rodas: "",
  especificacoes_adicionais: "",
};

const formatMoney = (v) =>
  (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function CatalogoEquipamentos() {
  const [items, setItems] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [filtroCat, setFiltroCat] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: catalogo }, { data: cats }] = await Promise.all([
        api.get("/proposta/catalogo"),
        api.get("/proposta/catalogo/categorias"),
      ]);
      setItems(catalogo || []);
      setCategorias(cats || []);
    } catch (e) {
      toast.error("Falha ao carregar catálogo");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const abrirNovo = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const abrirEdicao = (it) => {
    setEditing(it);
    setForm({
      nome: it.nome || "",
      categoria: it.categoria || "",
      descricao: it.descricao || "",
      valor_mensal_sugerido: it.valor_mensal_sugerido || 0,
      ativo: it.ativo !== false,
      capacidade_carga_kg: it.capacidade_carga_kg || "",
      elevacao_garfos_mm: it.elevacao_garfos_mm || "",
      largura_garfo_mm: it.largura_garfo_mm || "",
      comprimento_garfos_mm: it.comprimento_garfos_mm || "",
      tipo_rodas: it.tipo_rodas || "",
      especificacoes_adicionais: it.especificacoes_adicionais || "",
      foto_url: it.foto_url || null,
    });
    setOpen(true);
  };

  const _toIntOrNull = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
  };

  const salvar = async () => {
    if (!form.nome.trim()) {
      toast.error("Nome obrigatório");
      return;
    }
    const payload = {
      ...form,
      valor_mensal_sugerido: parseFloat(form.valor_mensal_sugerido) || 0,
      capacidade_carga_kg: _toIntOrNull(form.capacidade_carga_kg),
      elevacao_garfos_mm: _toIntOrNull(form.elevacao_garfos_mm),
      largura_garfo_mm: _toIntOrNull(form.largura_garfo_mm),
      comprimento_garfos_mm: _toIntOrNull(form.comprimento_garfos_mm),
    };
    delete payload.foto_url; // foto é gerenciada por endpoint separado
    try {
      if (editing) {
        await api.patch(`/proposta/catalogo/${editing.id}`, payload);
        toast.success("Item atualizado");
      } else {
        await api.post("/proposta/catalogo", payload);
        toast.success("Item criado");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    }
  };

  const remover = async (it) => {
    if (!window.confirm(`Remover "${it.nome}" do catálogo?`)) return;
    try {
      await api.delete(`/proposta/catalogo/${it.id}`);
      toast.success("Item removido");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao remover");
    }
  };

  const filtrados = items.filter((it) => {
    const okQ = !q || it.nome.toLowerCase().includes(q.toLowerCase()) ||
                (it.descricao || "").toLowerCase().includes(q.toLowerCase());
    const okCat = !filtroCat || it.categoria === filtroCat;
    return okQ && okCat;
  });

  return (
    <div className="space-y-5" data-testid="catalogo-equipamentos-page">
      <div className="flex items-end justify-between">
        <div className="flex items-center gap-3">
          <Boxes size={28} style={{ color: "#1B3A6B" }} />
          <div>
            <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>
              Catálogo de Equipamentos
            </h1>
            <p className="text-sm text-muted-foreground">
              Itens disponíveis para inclusão em propostas comerciais ({items.length})
            </p>
          </div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={abrirNovo} data-testid="novo-item-catalogo-btn" className="text-white" style={{ background: "#F97316" }}>
              <Plus size={14} className="mr-2" /> Novo item
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>{editing ? "Editar" : "Novo"} item do catálogo</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Nome do equipamento *</Label>
                <Input
                  data-testid="catalogo-nome"
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  placeholder="Ex: Empilhadeira Elétrica 2.000kg"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Categoria</Label>
                  <Input
                    data-testid="catalogo-categoria"
                    list="categorias-existentes"
                    value={form.categoria}
                    onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                    placeholder="Empilhadeira, Transpaleteira..."
                  />
                  <datalist id="categorias-existentes">
                    {categorias.map((c) => <option key={c} value={c} />)}
                  </datalist>
                </div>
                <div>
                  <Label>Valor mensal sugerido (R$)</Label>
                  <Input
                    data-testid="catalogo-valor"
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.valor_mensal_sugerido}
                    onChange={(e) => setForm({ ...form, valor_mensal_sugerido: e.target.value })}
                    placeholder="0,00"
                  />
                </div>
              </div>
              <div>
                <Label>Descrição / Resumo</Label>
                <Textarea
                  data-testid="catalogo-descricao"
                  rows={2}
                  value={form.descricao}
                  onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                  placeholder="Resumo curto que aparece na seleção de equipamentos"
                />
              </div>

              {/* Especificações técnicas — saem no PDF */}
              <div className="border-t pt-3 mt-2">
                <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "#1B3A6B" }}>
                  Especificações Técnicas (saem no PDF)
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Capacidade de carga (kg)</Label>
                    <Input
                      data-testid="catalogo-capacidade"
                      type="number"
                      min="0"
                      value={form.capacidade_carga_kg}
                      onChange={(e) => setForm({ ...form, capacidade_carga_kg: e.target.value })}
                      placeholder="2000"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Elevação dos garfos (mm)</Label>
                    <Input
                      data-testid="catalogo-elevacao"
                      type="number"
                      min="0"
                      value={form.elevacao_garfos_mm}
                      onChange={(e) => setForm({ ...form, elevacao_garfos_mm: e.target.value })}
                      placeholder="200"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Largura do garfo (mm)</Label>
                    <Input
                      data-testid="catalogo-largura"
                      type="number"
                      min="0"
                      value={form.largura_garfo_mm}
                      onChange={(e) => setForm({ ...form, largura_garfo_mm: e.target.value })}
                      placeholder="680"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Comprimento dos garfos (mm)</Label>
                    <Input
                      data-testid="catalogo-comprimento"
                      type="number"
                      min="0"
                      value={form.comprimento_garfos_mm}
                      onChange={(e) => setForm({ ...form, comprimento_garfos_mm: e.target.value })}
                      placeholder="1150"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Tipo de rodas</Label>
                    <Input
                      data-testid="catalogo-rodas"
                      value={form.tipo_rodas}
                      onChange={(e) => setForm({ ...form, tipo_rodas: e.target.value })}
                      placeholder="Nylon / PU / Borracha"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Especificações adicionais</Label>
                    <Textarea
                      data-testid="catalogo-specs-extra"
                      rows={2}
                      value={form.especificacoes_adicionais}
                      onChange={(e) => setForm({ ...form, especificacoes_adicionais: e.target.value })}
                      placeholder="Outras especificações que devem aparecer no PDF"
                    />
                  </div>
                </div>
              </div>

              {/* AJUSTE 4 — Upload padronizado de foto do equipamento */}
              {editing && (
                <div className="border-t pt-3">
                  <Label className="text-xs">Foto do equipamento (sai no PDF)</Label>
                  <div className="mt-1 p-2 bg-blue-50 border border-blue-200 rounded text-[10px] text-blue-900" data-testid="catalogo-foto-padrao">
                    <strong>Padrão recomendado:</strong> JPG, PNG ou WEBP · Tamanho máximo 5 MB ·
                    Dimensão ideal 1200×1200px (quadrada) · Fundo branco preferencial.
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    {form.foto_url ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL || ""}${form.foto_url}?t=${Date.now()}`}
                        alt="foto"
                        className="w-20 h-20 object-contain border rounded"
                        data-testid="catalogo-foto-preview-saved"
                      />
                    ) : (
                      <div className="w-20 h-20 border-2 border-dashed rounded flex items-center justify-center text-[10px] text-muted-foreground text-center px-1">
                        Sem foto<br/>cadastrada
                      </div>
                    )}
                    {form.preview_local && (
                      <div className="flex flex-col items-center">
                        <img
                          src={form.preview_local}
                          alt="preview"
                          className="w-20 h-20 object-contain border-2 border-amber-400 rounded"
                          data-testid="catalogo-foto-preview-local"
                        />
                        <span className="text-[10px] text-amber-700 mt-1">Pré-visualização</span>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      data-testid="catalogo-foto-input"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f || !editing?.id) return;

                        // AJUSTE 4 — Validação client-side de formato
                        const formatosOk = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
                        if (!formatosOk.includes(f.type)) {
                          toast.error("Formato não suportado. Use JPG, PNG ou WEBP.");
                          e.target.value = "";
                          return;
                        }
                        // AJUSTE 4 — Validação client-side de tamanho (5MB)
                        const limiteBytes = 5 * 1024 * 1024;
                        if (f.size > limiteBytes) {
                          const mb = (f.size / 1024 / 1024).toFixed(2);
                          toast.error(`Imagem muito grande (${mb} MB). O limite é 5 MB.`);
                          e.target.value = "";
                          return;
                        }
                        // Preview local antes do upload
                        const previewUrl = URL.createObjectURL(f);
                        setForm((prev) => ({ ...prev, preview_local: previewUrl }));

                        const fd = new FormData();
                        fd.append("file", f);
                        try {
                          const { data } = await api.post(`/catalogo/${editing.id}/foto`, fd, {
                            headers: { "Content-Type": "multipart/form-data" },
                          });
                          setForm((prev) => ({ ...prev, foto_url: data.foto_url, preview_local: null }));
                          URL.revokeObjectURL(previewUrl);
                          toast.success("Foto enviada com sucesso");
                          load();
                        } catch (err) {
                          URL.revokeObjectURL(previewUrl);
                          setForm((prev) => ({ ...prev, preview_local: null }));
                          const detail = err?.response?.data?.detail;
                          if (detail) {
                            toast.error(detail);
                          } else if (err?.response?.status === 413) {
                            toast.error("Imagem muito grande para o servidor.");
                          } else if (err?.response?.status === 400) {
                            toast.error("Formato não aceito pelo servidor.");
                          } else {
                            toast.error("Falha no upload — tente novamente");
                          }
                        }
                      }}
                      className="text-xs"
                    />
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">Salve o item antes de subir a foto.</p>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Switch
                  data-testid="catalogo-ativo"
                  checked={form.ativo}
                  onCheckedChange={(v) => setForm({ ...form, ativo: v })}
                />
                <Label>Ativo (aparece para vendedores)</Label>
              </div>
              <Button
                data-testid="catalogo-save"
                onClick={salvar}
                className="w-full text-white mt-2"
                style={{ background: "#1B3A6B" }}
              >
                {editing ? "Salvar alterações" : "Adicionar ao catálogo"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="catalogo-search"
              className="pl-9"
              placeholder="Buscar por nome ou descrição..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <select
            data-testid="catalogo-filtro-categoria"
            className="border rounded-md px-3 py-2 text-sm"
            value={filtroCat}
            onChange={(e) => setFiltroCat(e.target.value)}
          >
            <option value="">Todas categorias</option>
            {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </Card>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Equipamento</TableHead>
              <TableHead>Categoria</TableHead>
              <TableHead>Valor sugerido</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
            ) : filtrados.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Nenhum item encontrado.</TableCell></TableRow>
            ) : filtrados.map((it) => (
              <TableRow key={it.id} data-testid={`catalogo-row-${it.id}`}>
                <TableCell>
                  <div className="font-medium">{it.nome}</div>
                  {it.descricao && (
                    <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{it.descricao}</div>
                  )}
                </TableCell>
                <TableCell>
                  {it.categoria ? <Badge variant="outline">{it.categoria}</Badge> : <span className="text-muted-foreground">—</span>}
                </TableCell>
                <TableCell className="font-mono text-sm">{formatMoney(it.valor_mensal_sugerido)}</TableCell>
                <TableCell>
                  <Badge variant={it.ativo ? "default" : "outline"}>
                    {it.ativo ? "Ativo" : "Inativo"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" data-testid={`catalogo-edit-${it.id}`} onClick={() => abrirEdicao(it)}>
                    <Pencil size={14} />
                  </Button>
                  <Button size="sm" variant="ghost" data-testid={`catalogo-delete-${it.id}`} onClick={() => remover(it)}>
                    <Trash2 size={14} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
